/* La Sura - the builder's live cake preview.

   Reads the page (lasura-cake-read.js), never the other way round, and
   redraws with lasura-cake-draw.js on any change, animating between the two.
   With no script the block shows nothing.
   lasura-cake-camera.js turns the view; lasura-cake-in-view.js keeps the
   cake in view. */
class LasuraCakePreview extends HTMLElement {
  connectedCallback() {
    /* Moving the element reconnects it, and that second call sets it up. */
    if (this.place()) return;
    try {
      this.cfg = JSON.parse(this.querySelector('[data-preview-config]').textContent);
    } catch {
      return;
    }
    this.stage = this.querySelector('[data-preview-stage]');
    this.scope = this.closest('.shopify-section') || document;
    this.update = () => requestAnimationFrame(() => this.render());
    ['change', 'input', 'variant:update'].forEach((type) => document.addEventListener(type, this.update));
    this.viewNow ||= 0;
    this.render(true);
    window.LasuraCakeCamera?.watch(this);
    window.LasuraCakeInView?.setup(this);
    window.LasuraCakeTurn?.setup(this);
  }

  /* Draws at the camera's angle and the turn (lasura-cake-turn.js); carries a number cake's tip on. */
  paint(state, anim) {
    const { text, slice, messageOn } = this.cfg, since = performance.now() - this.tipAt;
    if (state.shape === 'number' && since < 700) anim = { ...anim, tip: Math.round(since) || 1 };
    this.stage.innerHTML = window.LasuraCakeDraw({ ...state, view: this.viewNow, spin: this.spin || 0 }, { ...text, slice, stage: true, anim, label: this.label, messageOn });
    this.shown = state;
    window.LasuraCakeInView?.paint(this, state, anim);
  }

  /* Builder pages (Matt, 2026-09-27): on two columns the preview takes the
     photo's column, pinned, beside the steps; on one column (phones, stacked
     tablets) it stays in the details, above the steps. The block's CSS shows
     or hides the photo column; this follows. A comment marks its home. */
  place() {
    const media = this.closest('.product-information__grid')?.querySelector('.product-information__media');
    if (!media) return false;
    if (!this.home) {
      this.home = document.createComment(' lasura-cake-preview ');
      this.before(this.home);
      /* Kept, or the list and its listener could be garbage collected. */
      this.widths = ['(min-width: 750px)', '(min-width: 990px)'].map((q) => matchMedia(q));
      this.widths.forEach((list) => list.addEventListener('change', () => this.place()));
    }
    const beside = getComputedStyle(media).display !== 'none';
    this.beside = beside;
    if (beside && this.parentElement !== media) media.prepend(this);
    else if (!beside && this.parentElement === media) this.home.after(this);
    else return false;
    return true;
  }

  disconnectedCallback() {
    if (!this.update) return;
    ['change', 'input', 'variant:update'].forEach((type) => document.removeEventListener(type, this.update));
    this.viewWatch?.disconnect();
    window.LasuraCakeInView?.teardown(this);
  }

  /* The choices on the page, read by lasura-cake-read.js. */
  state() {
    return window.LasuraCakeRead.state(this);
  }

  /* Draws the state. A new size or height grows or shrinks the cake on the
     stand, a new icing colour sweeps down from the top, other colours blend;
     what's added on top lands after. Reduced motion: sizes jump, colours blend. */
  render(first = false) {
    if (!window.LasuraCakeDraw) return;
    const s = this.state();
    const key = JSON.stringify(s);
    if (key === this.last) return;
    const p = this.prev;
    this.last = key;
    this.prev = s;
    const anim = first || !p ? {} : {
      /* A shape change is its own motion (the morph), so it doesn't settle. */
      settle: p.finish !== s.finish,
      tip: s.shape === 'number' && (p.shape !== 'number' || performance.now() - this.tipAt < 700),
      /* A cut-out topping (lasura-cake-draw-cutout.js) by its picture. */
      tops: [...s.toppings, ...(s.cutouts || []).map((c) => c.url)].filter((t) => !p.toppings.includes(t) && !(p.cutouts || []).some((c) => c.url === t)),
      gold: s.toppings.some((t) => t.startsWith('gold leaf')) && !p.toppings.some((t) => t.startsWith('gold leaf')),
      topper: p.topper !== s.topper,
      sheen: p.frost !== s.frost || p.frost2 !== s.frost2 || p.finish !== s.finish || p.drip !== s.drip,
      pour: s.finish === 'drip' && (p.finish !== 'drip' || p.drip !== s.drip),
      /* Letters added at the end are piped on; the index of the first. */
      typed: ((a, b) => (b.length > a.length && b.startsWith(a) ? a.length : -1))(...[p, s].map((x) => x.message.trim().replace(/\s+/g, ' '))),
      scrape: s.finish === 'semi-naked' && p.finish !== 'semi-naked',
      cut: p.sponge !== s.sponge || p.filling !== s.filling || p.layers !== s.layers
    };
    /* A number cake tips onto the plate; paint() carries the tip through a
       size move's repaints. */
    if (anim.tip && p && p.shape !== 'number') this.tipAt = performance.now();
    this.label = `${this.cfg.labelPrefix} ${s.names.join(', ')}${s.message.trim() ? `, "${s.message.trim()}"` : ''}.`;
    /* Shape or finish can rule out the top view. */
    window.LasuraCakeCamera?.sync(this);
    const paint = (state, a) => ((this.busy = false), this.paint(state, a));
    cancelAnimationFrame(this.frame);
    const from = this.shown;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* A new shape morphs out of the last (a number or letter just appears). */
    const morph = from && !reduce && from.shape !== s.shape && s.shape !== 'number' && from.shape !== 'number';
    const grow = from && !reduce && (morph || ['diameter', 'layers', 'slice'].some((k) => from[k] !== s[k]));
    const blend = from && ['frost', 'frost2', 'sponge', 'filling', 'drip'].some((k) => from[k] !== s[k] && from[k] && s[k]);
    const sweep = blend && !reduce && from.frost && s.frost && from.frost !== s.frost;
    if (first || !from || (!grow && !blend)) return paint(s, anim);
    const { mix } = window.LasuraCakeParts;
    const start = performance.now(), dur = morph ? 640 : grow ? 520 : sweep ? 750 : 260;
    const step = (now) => {
      /* Sizes ease out; a shape eases in and out, so its outline doesn't bulge early. */
      const t = Math.min(1, (now - start) / dur), e = 1 - (1 - t) ** 4, em = t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2;
      if (t >= 1) return paint(s, anim);
      const num = (k) => (grow ? from[k] + (s[k] - from[k]) * e : s[k]);
      this.busy = true;
      this.paint({ ...s, shapeFrom: morph ? from.shape : null, morph: em, diameter: num('diameter'), layers: num('layers'), slice: num('slice'), frost: sweep ? s.frost : mix(from.frost, s.frost, e), frostFrom: sweep && from.frost, sweep: sweep ? em : 1, frost2: mix(from.frost2, s.frost2, e), drip: mix(from.drip, s.drip, e), sponge: mix(from.sponge, s.sponge, e), filling: mix(from.filling, s.filling, e) }, {});
      this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('lasura-cake-preview')) {
  customElements.define('lasura-cake-preview', LasuraCakePreview);
}
